// Package Imports
/* eslint-disable @typescript-eslint/no-floating-promises */
import React, { createContext, useContext, useEffect, useState } from 'react';
import { useSetLocale } from '@refinedev/core';
import io, { Socket } from 'socket.io-client';

// Project Imports
import type ResourceItem from 'interfaces/resource';
import { Permission, Token, TokenContextType } from 'contexts/resource/type';
import fetchPermissions from 'core/utils/fetchPermissions';
import fetchOrganization from 'core/utils/fetchOrganization';
// eslint-disable-next-line import/no-extraneous-dependencies
import fetchPrograms from 'core/utils/fetchPrograms';
import axiosInstance from 'providers/axiosInstance/axiosInstance';
// eslint-disable-next-line import/no-extraneous-dependencies
// import { H } from 'highlight.run';
import useLogger from 'core/utils/useLogger';
import { REACT_APP_SOCKET_URL } from 'config';

const OrgIDKey = 'orgId';
const programIdKey = 'programId';

const TokenContext = createContext<TokenContextType>({
  token: undefined,
  setToken: () => {},
  userSessionId: undefined,
  setUserSessionId: () => {},
  crudPermissions: [],
  setCrudPermission: () => {},
  modulePermission: [],
  setModulePermission: () => {},
  userDetails: {},
  setUserDetails: () => {},
  filteredResources: [],
  setFilteredResources: () => {},
  loading: false,
  setLoading: () => {},
  orgId: '',
  setOrgId: () => {},
  programId: '',
  setProgramId: () => {},
  userProgramList: [],
  organizationList: [],
  setOrganizationDropdownList: () => {},
  currentLang: '',
  setCurrentLang: () => {},
  setRoles: () => {},
  roles: [],
  socketInstance: undefined,
  setSocketInstance: () => {},
});

export function useTokenContext() {
  return useContext(TokenContext);
}

const TokenProvider = ({ children }: { children: React.ReactNode }) => {
  const [token, setToken] = useState<Token>(undefined);
  const [userSessionId, setUserSessionId] = useState<string>(undefined);
  const [crudPermissions, setCrudPermission] = useState<Permission>([]);
  const [modulePermission, setModulePermission] = useState<Permission>([]);
  const [filteredResources, setFilteredResources] = useState<ResourceItem[]>(
    []
  );
  const [loading, setLoading] = useState(false);
  const [userDetails, setUserDetails] = useState<any>({});
  const [roles, setRoles] = useState([]);
  const [orgId, setOrgIdState] = useState<string | number>(() => {
    const storedOrgId = localStorage.getItem(OrgIDKey);
    return storedOrgId || '';
  });
  const [programId, setProgramIdState] = useState<string>(() => {
    const storedProgramId = localStorage.getItem(programIdKey);
    return storedProgramId || '';
  });
  const [organizationList, setOrganizationList] = useState<any[]>([]);
  const [userProgramList, setUserProgramList] = useState<any[]>([]);
  const [currentLang, setCurrentLang] = useState('en');
  const changeLanguage = useSetLocale();
  const [socketInstance, setSocketInstance] = useState<Socket | undefined>(
    undefined
  );

  const logger = useLogger();

  // Function to set orgId in both state and localStorage
  const setOrgId = (newOrgId: string) => {
    setOrgIdState(newOrgId);
  };

  // Function to set programId in both state and localStorage
  const setProgramId = (newProgramId: any) => {
    setProgramIdState(newProgramId);
  };

  const connect = (id, orgid) => {
    const userId = id;

    if (userId && orgid) {
      const newSocket = io(REACT_APP_SOCKET_URL, {
        query: { userId, orgid },
      });
      newSocket.on('connect', () => {
        logger('Connected to Socket.IO server with userId:', userId);
      });

      newSocket.emit('joinRoom', { roomId: `${userId}_${orgid}` });

      // Set the new socket instance
      setSocketInstance(newSocket);
    }
  };

  // H.init('56gl9g91', {
  //   serviceName: 'data-management',
  //   environment: 'production',
  //   disableConsoleRecording: false,
  //   version: '0.1',
  //   tracingOrigins: true,
  //   networkRecording: { enabled: true, recordHeadersAndBody: true },
  //   backendUrl: 'https://highlight.augment8.dev/public ',
  // });

  // H.identify(`${userDetails.user_mail_id}`, {
  //   id: userDetails?.unique_id,
  //   orgId: userDetails?.role_id,
  //   roleId: userDetails?.organization_id,
  //   email: userDetails?.email,
  //   name: userDetails?.name,
  //   role: userDetails?.role_name,
  // });

  // logger('H', H);

  const fetchData = async () => {
    setLoading(true);
    axiosInstance.defaults.headers.common = {
      Authorization: `Bearer ${token}`,
      'user-session-id': userSessionId,
    };
    await fetchOrganization(setOrganizationList);
    await fetchPermissions(
      setFilteredResources,
      setCrudPermission,
      setModulePermission,
      setUserDetails,
      changeLanguage,
      setCurrentLang,
      connect,
      currentLang,
      token,
      setOrgId,
      setProgramId,
      userSessionId
    );
    await fetchPrograms(setUserProgramList);
    setLoading(false);
  };

  useEffect(() => {
    if (token && userSessionId) {
      fetchData();
    }
  }, [token]);

  return (
    <TokenContext.Provider
      value={{
        token,
        setToken,
        crudPermissions,
        setCrudPermission,
        modulePermission,
        setModulePermission,
        filteredResources,
        setFilteredResources,
        userDetails,
        setUserDetails,
        loading,
        setLoading,
        orgId,
        setOrgId,
        setProgramId,
        programId,
        userProgramList,
        organizationList,
        setOrganizationDropdownList: setOrganizationList,
        currentLang,
        setCurrentLang,
        roles,
        setRoles,
        socketInstance,
        setSocketInstance,
        userSessionId,
        setUserSessionId,
      }}
    >
      {children}
    </TokenContext.Provider>
  );
};

export default TokenProvider;
